import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MemoryManager.css';

interface Memory {
  id: number;
  category: string;
  key: string;
  value: string;
  confidence: number;
  source: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

interface MemoryStats {
  stats: { [key: string]: number };
  archived_count: number;
  total: number;
}

interface MemoryManagerProps {
  apiUrl: string;
  userToken: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const MemoryManager: React.FC<MemoryManagerProps> = ({ apiUrl, userToken, isOpen, onClose }) => {
  const [memories, setMemories] = useState<Memory[]>([]);
  const [stats, setStats] = useState<MemoryStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'fact' | 'preference' | 'instruction'>('fact');
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingMemory, setEditingMemory] = useState<Memory | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMemory, setNewMemory] = useState({ key: '', value: '', category: 'fact' });

  useEffect(() => {
    if (isOpen && userToken) {
      fetchMemories();
      fetchStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userToken, activeTab, showArchived]);

  const fetchMemories = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiUrl}/memories`, {
        headers: { 'Authorization': `Bearer ${userToken}` },
        params: {
          category: activeTab,
          include_archived: showArchived
        }
      });
      setMemories(response.data.memories || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load memories');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${apiUrl}/memories/stats`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleAddMemory = async () => {
    if (!newMemory.key.trim() || !newMemory.value.trim()) return;

    const categoryToAdd = newMemory.category as 'fact' | 'preference' | 'instruction';

    console.log('Adding memory:', { key: newMemory.key, value: newMemory.value, category: categoryToAdd });

    try {
      const response = await axios.post(`${apiUrl}/memories`, {
        key: newMemory.key,
        value: newMemory.value,
        category: categoryToAdd
      }, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Memory added successfully:', response.data);
      setNewMemory({ key: '', value: '', category: 'fact' });
      setShowAddForm(false);
      // Switch to the tab of the category we just added so user sees it
      setActiveTab(categoryToAdd);
      fetchStats();
    } catch (err: any) {
      console.error('Failed to add memory:', err.response?.data || err.message);
      setError(err.response?.data?.detail || 'Failed to add memory');
    }
  };

  const handleUpdateMemory = async () => {
    if (!editingMemory) return;

    try {
      await axios.put(`${apiUrl}/memories/${editingMemory.id}`, null, {
        headers: { 'Authorization': `Bearer ${userToken}` },
        params: {
          key: editingMemory.key,
          value: editingMemory.value,
          category: editingMemory.category
        }
      });
      setEditingMemory(null);
      fetchMemories();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update memory');
    }
  };

  const handleArchiveMemory = async (memoryId: number, archive: boolean) => {
    try {
      await axios.put(`${apiUrl}/memories/${memoryId}`, null, {
        headers: { 'Authorization': `Bearer ${userToken}` },
        params: { is_archived: archive }
      });
      fetchMemories();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to archive memory');
    }
  };

  const handleDeleteMemory = async (memoryId: number) => {
    try {
      await axios.delete(`${apiUrl}/memories/${memoryId}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      setDeleteConfirm(null);
      fetchMemories();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete memory');
    }
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.8) return <span className="badge badge-high">High</span>;
    if (confidence >= 0.5) return <span className="badge badge-medium">Medium</span>;
    return <span className="badge badge-low">Low</span>;
  };

  const getSourceBadge = (source: string) => {
    const badges: { [key: string]: string } = {
      'chat': 'From Chat',
      'manual': 'Manual',
      'inferred': 'Inferred'
    };
    return <span className="badge badge-source">{badges[source] || source}</span>;
  };

  const filteredMemories = memories.filter(m =>
    m.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.value.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="memory-panel-overlay" onClick={onClose} />
      <div className={`memory-panel ${isOpen ? 'open' : ''}`}>
        <div className="memory-header">
          <h3>🧠 Memory Manager</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Stats Summary */}
        {stats && (
          <div className="memory-stats">
            <div className="stat-item">
              <span className="stat-number">{stats.stats.fact || 0}</span>
              <span className="stat-label">Facts</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.stats.preference || 0}</span>
              <span className="stat-label">Preferences</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.stats.instruction || 0}</span>
              <span className="stat-label">Instructions</span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="memory-tabs">
          <button
            className={`tab ${activeTab === 'fact' ? 'active' : ''}`}
            onClick={() => setActiveTab('fact')}
          >
            Facts
          </button>
          <button
            className={`tab ${activeTab === 'preference' ? 'active' : ''}`}
            onClick={() => setActiveTab('preference')}
          >
            Preferences
          </button>
          <button
            className={`tab ${activeTab === 'instruction' ? 'active' : ''}`}
            onClick={() => setActiveTab('instruction')}
          >
            Instructions
          </button>
        </div>

        {/* Search & Actions */}
        <div className="memory-actions">
          <input
            type="text"
            placeholder="Search memories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="memory-search"
          />
          <button
            className="add-memory-btn"
            onClick={() => setShowAddForm(true)}
          >
            + Add
          </button>
        </div>

        <div className="memory-options">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
        </div>

        {loading && <div className="memory-loading">Loading...</div>}
        {error && <div className="memory-error">{error}</div>}

        {!loading && filteredMemories.length === 0 && (
          <div className="memory-empty">
            <p>No {activeTab}s found</p>
            <small>Memories are automatically learned from your chats</small>
          </div>
        )}

        {/* Memory List */}
        <div className="memory-list">
          {filteredMemories.map(memory => (
            <div key={memory.id} className={`memory-card ${memory.is_archived ? 'archived' : ''}`}>
              {editingMemory?.id === memory.id ? (
                <div className="memory-edit-form">
                  <input
                    type="text"
                    value={editingMemory.key}
                    onChange={(e) => setEditingMemory({ ...editingMemory, key: e.target.value })}
                    placeholder="Key"
                    className="edit-input"
                  />
                  <textarea
                    value={editingMemory.value}
                    onChange={(e) => setEditingMemory({ ...editingMemory, value: e.target.value })}
                    placeholder="Value"
                    className="edit-textarea"
                  />
                  <div className="edit-actions">
                    <button onClick={handleUpdateMemory} className="save-btn">Save</button>
                    <button onClick={() => setEditingMemory(null)} className="cancel-btn">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="memory-content">
                    <div className="memory-key">{memory.key}</div>
                    <div className="memory-value">{memory.value}</div>
                    <div className="memory-badges">
                      {getConfidenceBadge(memory.confidence)}
                      {getSourceBadge(memory.source)}
                    </div>
                  </div>
                  <div className="memory-actions-row">
                    <button
                      onClick={() => setEditingMemory(memory)}
                      className="action-btn-small"
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleArchiveMemory(memory.id, !memory.is_archived)}
                      className="action-btn-small"
                      title={memory.is_archived ? "Unarchive" : "Archive"}
                    >
                      {memory.is_archived ? '📤' : '📥'}
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(memory.id)}
                      className="action-btn-small delete"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add Memory Form Modal */}
        {showAddForm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h4>Add New Memory</h4>
              <select
                value={newMemory.category}
                onChange={(e) => setNewMemory({ ...newMemory, category: e.target.value })}
                className="form-select"
              >
                <option value="fact">Fact</option>
                <option value="preference">Preference</option>
                <option value="instruction">Instruction</option>
              </select>
              <input
                type="text"
                placeholder="Key (e.g., 'name', 'favorite_color')"
                value={newMemory.key}
                onChange={(e) => setNewMemory({ ...newMemory, key: e.target.value })}
                className="form-input"
              />
              <textarea
                placeholder="Value"
                value={newMemory.value}
                onChange={(e) => setNewMemory({ ...newMemory, value: e.target.value })}
                className="form-textarea"
              />
              <div className="modal-actions">
                <button onClick={handleAddMemory} className="primary-btn">Add Memory</button>
                <button onClick={() => setShowAddForm(false)} className="secondary-btn">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="confirm-modal">
            <div className="confirm-content">
              <p>Delete this memory permanently?</p>
              <div className="confirm-actions">
                <button
                  className="confirm-yes"
                  onClick={() => handleDeleteMemory(deleteConfirm)}
                >
                  Delete
                </button>
                <button
                  className="confirm-no"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MemoryManager;
